import { createSlice } from '@reduxjs/toolkit';
import { createInvoice, fetchUserInvoices, fetchInvoiceDetails, createAdminInvoice, fetchAllInvoices, fetchAdminInvoiceDetails, updateInvoiceStatus, updateInvoiceTerms, fetchUserDraftInvoices, fetchAdminDraftInvoices, fetchAllFundedInvoices, attachInvoicePayment, updateUserInvoiceStatus } from './actions';

const initialState = {
  invoices: [],
  draftInvoices: [],
  selectedInvoice: null,
  loading: false,
  error: null,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  },
  fundedInvoices: [],
  fundedInvoicesPagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  },
};

const invoicesSlice = createSlice({
  name: 'invoices',
  initialState,
  reducers: {
    clearSelectedInvoice: (state) => {
      state.selectedInvoice = null;
    },
    setSelectedInvoice: (state, action) => {
      state.selectedInvoice = action.payload;
    }
  },
  extraReducers: (builder) => {
    // Fetch user invoices
    builder.addCase(fetchUserInvoices.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchUserInvoices.fulfilled, (state, action) => {
      state.invoices = action.payload.invoices;
      state.pagination = action.payload.pagination;
      state.loading = false;
    });
    builder.addCase(fetchUserInvoices.rejected, (state, action) => {
      state.error = action.payload;
      state.loading = false;
    });

    // Create invoice
    builder.addCase(createInvoice.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(createInvoice.fulfilled, (state, action) => {
      // state.invoices.unshift(action.payload);
      state.loading = false;
    });
    builder.addCase(createInvoice.rejected, (state, action) => {
      state.error = action.payload;
      state.loading = false;
    });

    // Fetch invoice details
    builder.addCase(fetchInvoiceDetails.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchInvoiceDetails.fulfilled, (state, action) => {
      state.selectedInvoice = action.payload;
      state.loading = false;
    });
    builder.addCase(fetchInvoiceDetails.rejected, (state, action) => {
      state.error = action.payload;
      state.loading = false;
    });

    // Admin Create invoice
    builder.addCase(createAdminInvoice.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(createAdminInvoice.fulfilled, (state, action) => {
      // state.invoices.unshift(action.payload);
      state.loading = false;
    });
    builder.addCase(createAdminInvoice.rejected, (state, action) => {
      state.error = action.payload;
      state.loading = false;
    });

    // Fetch all invoices (admin)
    builder.addCase(fetchAllInvoices.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchAllInvoices.fulfilled, (state, action) => {
      state.invoices = action.payload.invoices;
      state.pagination = action.payload.pagination;
      state.loading = false;
    });
    builder.addCase(fetchAllInvoices.rejected, (state, action) => {
      state.error = action.payload;
      state.loading = false;
    });

    // Fetch admin invoice details
    builder.addCase(fetchAdminInvoiceDetails.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchAdminInvoiceDetails.fulfilled, (state, action) => {
      state.selectedInvoice = action.payload;
      state.loading = false;
    });
    builder.addCase(fetchAdminInvoiceDetails.rejected, (state, action) => {
      state.error = action.payload;
      state.loading = false;
    });

    // Update invoice status
    builder.addCase(updateInvoiceStatus.fulfilled, (state, action) => {
      const updatedInvoice = action.payload;
      state.invoices = state.invoices.map(invoice =>
        invoice._id === updatedInvoice._id ? updatedInvoice : invoice
      );
      if (state.selectedInvoice?._id === updatedInvoice._id) {
        state.selectedInvoice = updatedInvoice;
      }
      state.loading = false;
    });

    builder.addCase(updateInvoiceStatus.rejected, (state, action) => {
      state.error = action.payload;
      state.loading = false;
    });

    // Update invoice terms
    builder.addCase(updateInvoiceTerms.fulfilled, (state, action) => {
      const updatedInvoice = action.payload;
      state.invoices = state.invoices.map(invoice =>
        invoice._id === updatedInvoice._id ? updatedInvoice : invoice
      );
      if (state.selectedInvoice?._id === updatedInvoice._id) {
        state.selectedInvoice = updatedInvoice;
      }
      state.loading = false;
    });

    // Fetch user draft invoices
    builder.addCase(fetchUserDraftInvoices.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchUserDraftInvoices.fulfilled, (state, action) => {
      state.draftInvoices = action.payload.invoices;
      state.loading = false;
    });
    builder.addCase(fetchUserDraftInvoices.rejected, (state, action) => {
      state.error = action.payload;
      state.loading = false;
    });

    // Fetch admin draft invoices
    builder.addCase(fetchAdminDraftInvoices.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchAdminDraftInvoices.fulfilled, (state, action) => {
      state.draftInvoices = action.payload.invoices;
      state.loading = false;
    });
    builder.addCase(fetchAdminDraftInvoices.rejected, (state, action) => {
      state.error = action.payload;
      state.loading = false;
    });

    // Fetch funded invoices
    builder.addCase(fetchAllFundedInvoices.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchAllFundedInvoices.fulfilled, (state, action) => {
      state.fundedInvoices = action.payload.invoices;
      state.fundedInvoicesPagination = action.payload.pagination;
      state.loading = false;
    });
    builder.addCase(fetchAllFundedInvoices.rejected, (state, action) => {
      state.error = action.payload;
      state.loading = false;
    });

    // Attach invoice payment
    builder.addCase(attachInvoicePayment.fulfilled, (state, action) => {
      const updatedInvoice = action.payload;
      state.invoices = state.invoices.map(invoice =>
        invoice._id === updatedInvoice._id ? updatedInvoice : invoice
      );
      if (state.selectedInvoice?._id === updatedInvoice._id) {
        state.selectedInvoice = updatedInvoice;
      }
      state.loading = false;
    });

    builder.addCase(attachInvoicePayment.rejected, (state, action) => {
      state.error = action.payload;
      state.loading = false;
    });

    // Update user invoice status
    builder.addCase(updateUserInvoiceStatus.fulfilled, (state, action) => {
      const updatedInvoice = action.payload;
      state.invoices = state.invoices.map(invoice =>
        invoice._id === updatedInvoice._id ? updatedInvoice : invoice
      );
      if (state.selectedInvoice?._id === updatedInvoice._id) {
        state.selectedInvoice = updatedInvoice;
      }
      state.loading = false;
    });

    builder.addCase(updateUserInvoiceStatus.rejected, (state, action) => {
      state.error = action.payload;
      state.loading = false;
    });
  },
});

export const { clearSelectedInvoice, setSelectedInvoice } = invoicesSlice.actions;
export default invoicesSlice.reducer;
